import { useSelector } from 'react-redux';
import { RootState } from '../reducers';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect } from 'react';
import extractErrorMsg from '../components/helpers/extractErrorMsg';
import useQueryParams from '../navigation-utils/useQueryParams';
import { setUserForHubspot } from '../services/hubspot';
import { appRoutes } from '../constants/appRoutes';
import { useLocation } from 'react-router';
import useUserInfo from './useUserInfo';
import { useRunOnceWhenTruthy } from '../components/common/useRunOnceWhenTruthy';
import { useCookies } from 'react-cookie';
import {
  AUTH_INVITATION_QUERY_PARAM_NAME,
  AUTH_ORG_QUERY_PARAM_NAME,
  IS_INVITATION_QUERY_PARAM_NAME,
} from '../auth/authConsts';
import {
  ACTIVATE_TRIAL_COOKIE_NAME,
  ACTIVATE_TRIAL_QUERY_PARAM,
} from '../components/dashboard/paywall/paywallConsts';
import isEqual from 'lodash/isEqual';
import { telemetry } from '../telemetry';
import { useAccountSelectionContext } from '../components/account-selection/AccountSelectionContext';

interface AuthErrorCheckResult {
  isMatch: boolean;
  data?: {
    userId?: string;
    userEmail?: string;
  };
}

const checkIsAuthError = (
  targetMessage: string,
  authError: Error | undefined,
  query: URLSearchParams,
): AuthErrorCheckResult => {
  const errorMessage = (authError as any)?.message || '';
  const errorDescription = (authError as any)?.error_description || '';

  // Try to parse JSON error description if it exists
  let authErrorObj;
  const errorDescriptionFromQuery = query.get('error_description');

  // Only try to parse as JSON if it looks like JSON (starts with '{' or '[')
  if (errorDescription && (errorDescription.startsWith('{') || errorDescription.startsWith('['))) {
    try {
      authErrorObj = JSON.parse(errorDescription);
    } catch (e) {
      telemetry.captureError(e);
    }
  }

  // If no valid JSON from authError, try from query params (for verification flow)
  if (
    !authErrorObj &&
    errorDescriptionFromQuery &&
    (errorDescriptionFromQuery.startsWith('{') || errorDescriptionFromQuery.startsWith('['))
  ) {
    try {
      authErrorObj = JSON.parse(errorDescriptionFromQuery);
    } catch (e) {
      telemetry.captureError(e);
    }
  }

  // Check for exact match in all error sources
  const isMatch =
    (authErrorObj && authErrorObj.error === targetMessage) ||
    errorMessage === targetMessage ||
    errorDescription === targetMessage ||
    errorDescriptionFromQuery === targetMessage;

  const data = authErrorObj
    ? {
        userId: authErrorObj.userId as string | undefined,
        userEmail: authErrorObj.userEmail as string | undefined,
      }
    : undefined;

  return { isMatch, data };
};

const useCheckPersonalEmail = () => {
  const { error: authError } = useAuth0();
  const query = useQueryParams();

  const { isMatch } = checkIsAuthError('Please use a work email to sign up', authError, query);
  return isMatch;
};

const useCheckAuthVerification = () => {
  const { error: authError } = useAuth0();
  const query = useQueryParams();

  const { isMatch: isVerificationNeeded, data } = checkIsAuthError(
    'Email must be verified',
    authError,
    query,
  );

  return {
    isVerificationNeeded,
    authUserId: data?.userId,
    authUserEmail: data?.userEmail,
  };
};

const useAuthenticateUser = () => {
  const userProps = useSelector((state: RootState) => state.userInfo, isEqual);
  const { data: userInfo, isLoading, error, isNoAccountSetError } = userProps;
  const query = useQueryParams();
  const location = useLocation();
  const { fetchCurrentUser } = useUserInfo();
  const [, setCookie, removeCookie] = useCookies();
  const activateTrialQuery = query.has(ACTIVATE_TRIAL_QUERY_PARAM);
  const { setIsAuthZeroAccessDenied } = useAccountSelectionContext();

  //need to set a cookie because "email verification" flow doesn't support authorizationParams (so we don't have appState.returnTo)
  useRunOnceWhenTruthy(() => {
    setCookie(ACTIVATE_TRIAL_COOKIE_NAME, true, { path: '/' });
  }, activateTrialQuery);

  const {
    isLoading: authIsLoading,
    isAuthenticated,
    loginWithRedirect,
    error: authError,
    user: authUser,
  } = useAuth0();

  const isPersonalEmail = useCheckPersonalEmail();
  const notVerifiedAuthUserDetails = useCheckAuthVerification();

  //create/update the contact in hubspot
  useRunOnceWhenTruthy(() => {
    if (authUser) {
      setUserForHubspot(authUser);
    }
  }, !!authUser);

  const apiGetUser = useCallback(async () => {
    const user = await fetchCurrentUser();
    if (!user) {
      return;
    }
  }, [fetchCurrentUser]);

  const login = useCallback(async () => {
    const invitation = query.get(AUTH_INVITATION_QUERY_PARAM_NAME);
    const organization = query.get(AUTH_ORG_QUERY_PARAM_NAME);
    const isInvitation = !!invitation && !!organization;
    const funnelMapIdToNavigateTo = query.get('funnelMapId'); //This is relevant only for user email invitations link
    const accountId = query.get('accountId');
    const isRedirectToSignUp = location.pathname === '/signup' || query.get('signup') === 'true';

    const baseReturnTo = funnelMapIdToNavigateTo
      ? `${appRoutes.funnelEditor.route}/${funnelMapIdToNavigateTo}/${accountId}`
      : window.location.pathname;

    //In case of signup from invitation
    if (isInvitation) {
      query.set(IS_INVITATION_QUERY_PARAM_NAME, 'true');
      query.delete(AUTH_INVITATION_QUERY_PARAM_NAME);
      query.delete(AUTH_ORG_QUERY_PARAM_NAME);

      await loginWithRedirect({
        authorizationParams: {
          invitation,
          organization,
        },
        appState: {
          returnTo: baseReturnTo + '?' + query.toString(),
        },
      });
      return;
    }
    //In case of regular signup
    if (isRedirectToSignUp) {
      // Remove signup query param before redirecting
      query.delete('signup');
      await loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
        },
        appState: {
          returnTo: baseReturnTo + (query.toString() ? '?' + query.toString() : ''),
        },
      });
      return;
    }

    //In case of sign in
    await loginWithRedirect({
      appState: {
        returnTo: baseReturnTo + '?' + query.toString(),
      },
    });
  }, [query, location.pathname, loginWithRedirect]);

  useRunOnceWhenTruthy(() => {
    if (isAuthenticated) {
      apiGetUser();
    }
  }, isAuthenticated);

  useRunOnceWhenTruthy(() => {
    if (authError?.message?.match(/^user\s+\S+\s+is not part of the\s+\S+\s+organization$/)) {
      removeCookie('orgId');
      setIsAuthZeroAccessDenied(true);
    }
  }, !!authError);

  useEffect(() => {
    if (
      !isAuthenticated &&
      !authIsLoading &&
      !authError &&
      !isPersonalEmail &&
      !notVerifiedAuthUserDetails.isVerificationNeeded
    ) {
      login();
    }
  }, [
    authIsLoading,
    authError,
    isAuthenticated,
    isPersonalEmail,
    notVerifiedAuthUserDetails.isVerificationNeeded,
    login,
  ]);

  return {
    userInfo,
    error: error || extractErrorMsg(authError),
    isLoading: authIsLoading || isLoading,
    isPersonalEmail,
    notVerifiedAuthUserDetails,
    isNoAccountSetError,
  };
};

export { useAuthenticateUser };
